import './TpvModalForm.component.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ERoutes } from '../../../assets/constants/routes';
import { EEndpoint } from '../../../config/endpoint.config';
import { TPV_URL_LOADED } from '../../../store/bank/bank.actions';
import { makeSelectTpvUrl } from '../../../store/bank/bank.selectors';
import { makeSelectCurrentUser } from '../../../store/current-user/current-user.selectors';
import {
  normalizeCurrency,
  formatCurrencyToNumber,
} from '../../../utils/FormNormalizers';
import { HttpService } from '../../../services/services.index';

enum EPaymentType {
  card = 'card',
  bizum = 'bizum',
}

type TPaymentType = EPaymentType.card | EPaymentType.bizum;

export function TpvModalFormComponent(props: any) {
  const {
    showModal,
    handleClose,
    pendingPaymentId,
    variableAmount,
    amount: propsAmount,
  } = props;
  const dispatch = useDispatch();
  const httpService = new HttpService();
  const tpvUrl = useSelector(makeSelectTpvUrl());
  const currenUser = useSelector(makeSelectCurrentUser());
  const [iframeRef, setIframeRef] = useState<HTMLIFrameElement>();
  const URL_OK_CARD = `${process.env.REACT_APP_BASE_URL}${ERoutes.TPV_OK}`;
  const URL_KO_CARD = `${process.env.REACT_APP_BASE_URL}${ERoutes.TPV_KO}`;
  const [paymentType, setPaymentType] = useState<TPaymentType | undefined>();
  const [isVariableAmount, setIsVariableAmount] = useState<boolean>(
    variableAmount,
  );
  const [inputValue, setInputValue] = useState<string | undefined>('');
  const amount =
    variableAmount && !!propsAmount && propsAmount > 0
      ? propsAmount
      : undefined;

  useEffect(() => {
    if (!pendingPaymentId || !paymentType) {
      return;
    }
    let TPV_URL;
    const URL_BIZUM = window.location.href.includes('#')
      ? window.location.href.slice(0, window.location.href.indexOf('#'))
      : window.location.href;
    switch (paymentType) {
      case EPaymentType.card:
        TPV_URL = `${process.env.REACT_APP_TPV_URL}${EEndpoint.GET_TPV_URL}?pendingPaymentId=${pendingPaymentId}&urlOk=${URL_OK_CARD}&urlKo=${URL_KO_CARD}&type=${paymentType}&requestBy=${currenUser.id}`;
        break;
      case EPaymentType.bizum:
        TPV_URL = `${process.env.REACT_APP_TPV_URL}${EEndpoint.GET_TPV_URL}?pendingPaymentId=${pendingPaymentId}&urlOk=${URL_BIZUM}&urlKo=${URL_BIZUM}&type=${paymentType}&requestBy=${currenUser.id}`;
        break;
      default:
        break;
    }
    dispatch({
      type: TPV_URL_LOADED,
      payload: TPV_URL,
    });
    // eslint-disable-next-line
  }, [paymentType]);

  useEffect(() => {
    if (!!tpvUrl && paymentType === EPaymentType.bizum) {
      resetModal();
      window.open(tpvUrl, '_self');
    } else {
      return;
    }
    // eslint-disable-next-line
  }, [tpvUrl]);

  function resetModal() {
    setPaymentType(undefined);
    setInputValue('');
    setIsVariableAmount(variableAmount);
    handleClose();
  }

  function urlCatcher(iFrame: any) {
    try {
      const iframeSrc = iFrame.contentWindow.location.href;
      if (
        (iframeSrc.includes(ERoutes.TPV_OK) ||
          iframeSrc.includes(ERoutes.TPV_KO)) &&
        !iframeSrc.includes('?pendingPaymentId=')
      ) {
        resetModal();
      }
    } catch {
      return false;
    }
  }

  async function handleSubmit() {
    try {
      await httpService.patch(
        EEndpoint.SET_VARIABLE_AMOUNT,
        { amount: formatCurrencyToNumber(inputValue as string) },
        {
          params: { pendingPaymentId: pendingPaymentId },
          silently: true,
        },
      );
    } catch {
      resetModal();
    } finally {
      setIsVariableAmount(false);
    }
  }

  return (
    <Modal
      className='tpv-modal'
      show={showModal}
      onHide={() => resetModal()}
      size='xl'
      centered
    >
      <Modal.Body>
        {isVariableAmount && (
          <React.Fragment>
            <div className='payment-type-title d-flex justify-content-center align-items-center mb-3'>
              <h6 className='subtitle'>
                <FormattedMessage
                  id={`organisms.tpv-modal-form.variable-amount.title`}
                />
              </h6>
            </div>
            <div className='field-container mb-5'>
              <FormattedMessage
                id={
                  !!amount
                    ? 'organisms.tpv-modal-form.variable-amount.placeholder-has-amount'
                    : 'organisms.tpv-modal-form.variable-amount.placeholder-no-amount'
                }
                values={{ value: normalizeCurrency(amount) }}
              >
                {(txt: any) => (
                  <input
                    name={'amount'}
                    id='variableAmount'
                    className='field'
                    placeholder={txt}
                    value={inputValue}
                    onChange={(e: any) =>
                      e.target.value === '€ '
                        ? setInputValue('')
                        : setInputValue(normalizeCurrency(e.target.value))
                    }
                    autoComplete='false'
                  />
                )}
              </FormattedMessage>
            </div>
            <div className='buttons-container'>
              <button className='btn-lili danger' onClick={() => resetModal()}>
                <FormattedMessage id='common.cancel' />
              </button>
              <button
                className='btn-lili'
                onClick={() => handleSubmit()}
                disabled={!inputValue}
              >
                <FormattedMessage id='common.pay' />
              </button>
            </div>
          </React.Fragment>
        )}
        {!paymentType && !isVariableAmount && (
          <React.Fragment>
            <div className='payment-type-title d-flex justify-content-center align-items-center'>
              <h6 className='subtitle'>
                <FormattedMessage
                  id={`pages.pending-payments.payment-type-title`}
                />
              </h6>
            </div>
            <div className='payment-type-container d-flex justify-content-center align-items-center'>
              <div
                className='payment md-card d-flex flex-column justify-content-center align-items-center'
                onClick={() => setPaymentType(EPaymentType.card)}
              >
                <div className='icon'>
                  <FontAwesomeIcon icon='credit-card' />
                </div>
                <div className='text'>
                  <FormattedMessage id='pages.pending-payments.payment-type-card' />
                </div>
              </div>
              <div
                className='payment md-card d-flex flex-column justify-content-center align-items-center disabled'
                onClick={() => setPaymentType(EPaymentType.bizum)}
              >
                <div className='icon'>
                  <FontAwesomeIcon icon='mobile-alt' />
                </div>
                <div className='text'>
                  <FormattedMessage id='pages.pending-payments.payment-type-bizum' />
                </div>
              </div>
            </div>
          </React.Fragment>
        )}

        {paymentType === EPaymentType.card && !isVariableAmount && (
          <React.Fragment>
            <div className='back-container d-flex'>
              <div
                className='back-item d-flex align-items-center'
                onClick={() => setPaymentType(undefined)}
              >
                <div className='icon mr-2'>
                  <FontAwesomeIcon icon='chevron-left' />
                </div>
                <div className='text'>
                  <FormattedMessage id='pages.pending-payments.payment-type-back' />
                </div>
              </div>
            </div>
            <div className='spinner-container d-flex justify-content-center align-items-center'>
              <span className='spinner-border text-primary' role='status'>
                <span className='sr-only'>Loading...</span>
              </span>
            </div>
            <iframe
              src={tpvUrl}
              name='redsys-iframe'
              ref={(r: any) => setIframeRef(r)}
              onLoad={() => (!!iframeRef ? urlCatcher(iframeRef) : undefined)}
              width='100%'
              height='617px'
              title='tpv'
            />
          </React.Fragment>
        )}
      </Modal.Body>
    </Modal>
  );
}
